import { ChevronsUpDown, Languages, LogOut, Settings, UserCog } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useLogout, useMe } from "@/lib/api";
import { currentPersona, DEV_PERSONAS, personaHref } from "@/lib/dev-personas";
import {
  isLanguage,
  LANGUAGE_NAMES,
  LANGUAGES,
  setLanguage,
  useLanguage,
} from "@/lib/i18n/language";
import { logger } from "@/lib/logger";
import { focusMainContent } from "@/lib/main-content";
import { SETTINGS_PATH } from "@/lib/navigation";
import { roleLabel } from "@/lib/roles";
import { cn } from "@/lib/utils";
import { realRole, shownAccountEmail, shownAccountInitial, shownAccountName } from "@/lib/view-as";

interface AccountMenuProps {
  /** Runs when the menu leaves the page (Settings, Sign out); the phone's sheet closes on it. */
  onNavigate?: () => void;
  /** In the collapsed sidebar: the avatar alone, with the name as a tooltip. */
  collapsed?: boolean;
}

/**
 * The account menu at the bottom of the sidebar: the avatar's initial, the
 * name and a chevron, opening upwards to the email, Settings, the language
 * (each in its own name, as radio items) and Sign out. On a local machine
 * with the dev auth bypass on (`/auth/me` says `dev_bypass`), it also offers
 * "Bekijken als": the four personas, one per role, each a full-page switch
 * (`personaHref`), so a reviewer sees every page as each role. While a
 * developer views the dashboard as someone else, it names the viewed person,
 * as `/auth/me` does, and a viewed role by its label ("Lid"), never by its
 * made-up address. In the collapsed
 * sidebar only the avatar shows, in the same place; the name stays the
 * button's for screen readers and shows as a tooltip.
 * Radix makes it a keyboard menu (arrows, Enter, Escape) and hands focus back
 * to the trigger on close, except when Settings opens another page: then
 * focus moves to that page's content.
 */
export function AccountMenu({ onNavigate, collapsed = false }: AccountMenuProps) {
  const { data: me } = useMe();
  const logoutMutation = useLogout();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Set when Settings opens another page, whose content then owns focus.
  const leavingRef = useRef(false);
  const { t } = useTranslation();
  const language = useLanguage();

  const name = me ? shownAccountName(me) : t("accountMenu.fallbackName");
  const initial = me ? shownAccountInitial(me) : "";
  const email = me ? shownAccountEmail(me) : null;

  const signOut = async () => {
    onNavigate?.();
    try {
      await logoutMutation.mutateAsync();
      logger.debug("[auth]", "signed out → /login");
      navigate("/login");
    } catch (err) {
      logger.warn("[auth] sign-out failed", err);
    }
  };

  return (
    // The tooltip wraps the menu, and its trigger sits inside the menu's, so the
    // menu's `data-state` (open or closed) is the one the button carries.
    <Tooltip>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-ring data-[state=open]:bg-sidebar-accent flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
            >
              <Avatar size="sm" aria-hidden="true">
                <AvatarFallback>{initial}</AvatarFallback>
              </Avatar>
              <span className={cn("min-w-0 flex-1 truncate", collapsed && "sr-only")}>{name}</span>
              {!collapsed && (
                <ChevronsUpDown className="text-sidebar-muted size-4 shrink-0" aria-hidden="true" />
              )}
            </button>
          </TooltipTrigger>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="top"
          align="start"
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
          onCloseAutoFocus={(event) => {
            if (leavingRef.current) {
              leavingRef.current = false;
              event.preventDefault();
              focusMainContent();
            }
          }}
        >
          {email && (
            <>
              <DropdownMenuLabel className="text-muted truncate font-normal">
                {email}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem asChild>
            <Link
              to={SETTINGS_PATH}
              onClick={() => {
                leavingRef.current = pathname !== SETTINGS_PATH;
                onNavigate?.();
              }}
            >
              <Settings aria-hidden="true" />
              {t("nav.settings")}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Languages aria-hidden="true" />
              <span className="flex-1">{t("language.label")}</span>
              <span lang={language} className="text-muted text-xs">
                {LANGUAGE_NAMES[language]}
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup
                  value={language}
                  onValueChange={(value) => {
                    if (isLanguage(value)) {
                      setLanguage(value);
                    }
                  }}
                >
                  {LANGUAGES.map((code) => (
                    <DropdownMenuRadioItem key={code} value={code} lang={code}>
                      {LANGUAGE_NAMES[code]}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          {me?.dev_bypass && <PersonaSwitch role={realRole(me)} />}
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={logoutMutation.isPending} onSelect={() => void signOut()}>
            <LogOut aria-hidden="true" />
            {t("accountMenu.signOut")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {collapsed && (
        <TooltipContent side="right" sideOffset={8}>
          {name}
        </TooltipContent>
      )}
    </Tooltip>
  );
}

/**
 * "Persona (lokaal)", local only: the dev auth bypass's personas as radio items,
 * the current one by its role. Choosing one loads the switch's link, which
 * signs the bypass in as that persona and opens Home, so every query starts
 * afresh.
 */
function PersonaSwitch({ role }: { role: string }) {
  const { t } = useTranslation();
  const current = currentPersona(role);
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <UserCog aria-hidden="true" />
        <span className="flex-1">{t("accountMenu.persona")}</span>
        {current && <span className="text-muted text-xs">{roleLabel(current.role)}</span>}
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent>
          <DropdownMenuLabel className="text-muted text-xs font-normal">
            {t("accountMenu.personaNote")}
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={current?.slug ?? ""}
            onValueChange={(slug) => {
              logger.debug("[auth]", "dev persona →", slug);
              window.location.assign(personaHref(slug));
            }}
          >
            {DEV_PERSONAS.map((persona) => (
              <DropdownMenuRadioItem key={persona.slug} value={persona.slug}>
                {roleLabel(persona.role)}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}
